import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, Cell, ReferenceLine
} from 'recharts';
import {
  MAANEDER, MAANED_KORT, INNTEKTER, FASTE_UTGIFTER, KREDITTKORT,
  AAR, formatNOK
} from './data';
import KategoriTransaksjoner from './KategoriTransaksjoner';

// Stiltokens — speiler C, card og mono i App.js
const C = {
  bg: '#F0F2F5', surface: '#FFFFFF', surface2: '#E8ECF0',
  border: '#CBD2DA', text: '#1A2332', muted: '#6B7888',
  green: '#2D7D9A', greenL: '#E3F2F8', red: '#C0392B', redL: '#FBEAE8',
  blue: '#2C5F8A', blueL: '#E3EDF6', amber: '#7D6608', amberL: '#FEF9E7',
  dark: '#1A2332',
};
const card = (extra = {}) => ({
  background: C.surface, border: `1px solid ${C.border}`,
  borderRadius: 10, padding: '18px 20px', ...extra
});
const mono = { fontFamily: "'DM Mono', monospace" };

function useIsMobile() {
  const [w, setW] = React.useState(window.innerWidth);
  React.useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return w <= 768;
}

const formatProsent = (p) => p === null ? '—' : `${p.toFixed(1).replace('.', ',')} %`;

// ══════════════════════════════════════════════════════════════════════════
// CASH FLOW / CFO — varsler, inntekt, forbruk, netto og sparerate per måned
// Faktiske tall der de finnes (fra budsjett.json), budsjett som fallback —
// samme mønster som Årsprognose.
// ══════════════════════════════════════════════════════════════════════════
export default function CashFlow({ token, getFaktisk, gjennomfortTom }) {
  const isMobile = useIsMobile();

  // Innstillinger (sparemål % og varselgrense %) — lagres i budsjett.json
  const [sparemaal, setSparemaal] = useState(null);
  const [varselProsent, setVarselProsent] = useState(null);
  const [redigerer, setRedigerer] = useState(null); // 'sparemaal' | 'varsel' | null
  const [inputVerdi, setInputVerdi] = useState('');
  const [lagrer, setLagrer] = useState(false);
  const [feil, setFeil] = useState(null);

  // Klikkbar kategori → modal med underliggende transaksjoner
  const [txKategori, setTxKategori] = useState(null);

  // CFO-notat (generert via Anthropic API på serveren)
  const [notat, setNotat] = useState(null);
  const [genererer, setGenererer] = useState(false);
  const [notatFeil, setNotatFeil] = useState(null);

  // Oppfølgingsspørsmål til notatet — tråden lagres i budsjett.json (cfoTraad)
  const [cfoTraad, setCfoTraad] = useState([]);
  const [cfoSporsmal, setCfoSporsmal] = useState('');
  const [sporLaster, setSporLaster] = useState(false);

  const stillSporsmal = async () => {
    const tekst = cfoSporsmal.trim();
    if (!tekst || sporLaster) return;
    setCfoSporsmal('');
    const nyTraad = [...cfoTraad, { rolle: 'bruker', tekst }];
    setCfoTraad(nyTraad);
    setSporLaster(true);
    try {
      const r = await fetch('/api/cfo/sporsmal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-token': token },
        body: JSON.stringify({ sporsmal: tekst }),
      }).then(r => r.json());
      if (r.ok && Array.isArray(r.traad)) setCfoTraad(r.traad);
      else setCfoTraad([...nyTraad, { rolle: 'assistent', tekst: r.svar || r.error || 'Ukjent feil' }]);
    } catch {
      setCfoTraad([...nyTraad, { rolle: 'assistent', tekst: 'Kunne ikke nå serveren. Prøv igjen.' }]);
    }
    setSporLaster(false);
  };

  const nullstillTraad = async () => {
    if (!window.confirm('Slette hele CFO-samtalen? Dette kan ikke angres.')) return;
    try {
      const r = await fetch('/api/cfo/traad', { method: 'DELETE', headers: { 'x-token': token } }).then(r => r.json());
      if (r.ok) setCfoTraad([]);
    } catch {}
  };

  useEffect(() => {
    fetch('/api/cfo/innstillinger', { headers: { 'x-token': token } })
      .then(r => r.json())
      .then(d => {
        if (typeof d.sparemaalProsent === 'number') setSparemaal(d.sparemaalProsent);
        if (typeof d.varselProsent === 'number') setVarselProsent(d.varselProsent);
      })
      .catch(() => {});
    fetch('/api/cfo/notat', { headers: { 'x-token': token } })
      .then(r => r.json())
      .then(d => { if (d.notat?.tekst || d.notat?.seksjoner) setNotat(d.notat); })
      .catch(() => {});
    fetch('/api/cfo/traad', { headers: { 'x-token': token } })
      .then(r => r.json())
      .then(d => { if (Array.isArray(d.traad)) setCfoTraad(d.traad); })
      .catch(() => {});
  }, [token]);

  const lagreInnstilling = async (felt) => {
    const n = parseFloat(String(inputVerdi).replace(',', '.'));
    const maks = felt === 'varselProsent' ? 500 : 100;
    if (!Number.isFinite(n) || n < 0 || n > maks) { setFeil(`Angi et tall mellom 0 og ${maks}`); return; }
    setLagrer(true); setFeil(null);
    try {
      const r = await fetch('/api/cfo/innstillinger', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-token': token },
        body: JSON.stringify({ [felt]: n }),
      }).then(r => r.json());
      if (r.ok) {
        setSparemaal(r.sparemaalProsent);
        setVarselProsent(r.varselProsent);
        setRedigerer(null);
      } else setFeil(r.error || 'Lagring feilet');
    } catch { setFeil('Nettverksfeil ved lagring'); }
    setLagrer(false);
  };

  const startRediger = (hva, gjeldende, standard) => {
    setInputVerdi(gjeldende !== null ? String(gjeldende) : String(standard));
    setFeil(null);
    setRedigerer(hva);
  };

  const editor = (felt) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <input
        type="number" min="0" max={felt === 'varselProsent' ? 500 : 100} step="0.5" value={inputVerdi}
        onChange={e => setInputVerdi(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') lagreInnstilling(felt); }}
        autoFocus
        style={{ width: 70, padding: '6px 8px', borderRadius: 6, border: `1px solid ${C.green}`, ...mono, fontSize: 14, textAlign: 'right' }}
      />
      <span style={{ fontSize: 13, color: C.muted }}>%</span>
      <button onClick={() => lagreInnstilling(felt)} disabled={lagrer} style={{ background: C.green, color: '#fff', border: 'none', borderRadius: 6, padding: '7px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 500 }}>
        {lagrer ? 'Lagrer…' : 'Lagre'}
      </button>
      <button onClick={() => { setRedigerer(null); setFeil(null); }} style={{ background: 'transparent', color: C.muted, border: `1px solid ${C.border}`, borderRadius: 6, padding: '7px 10px', fontSize: 12, cursor: 'pointer' }}>✕</button>
    </div>
  );

  // Per måned: faktisk der det finnes, ellers budsjett (per kategori)
  const maaneder = MAANEDER.map((m, i) => {
    let harFaktisk = false;
    const summer = (obj) => Object.entries(obj).reduce((s, [k, v]) => {
      const f = getFaktisk(k, i);
      if (f !== null) { harFaktisk = true; return s + f; }
      return s + (v.budsjett[i] || 0);
    }, 0);
    const inn = summer(INNTEKTER);
    const forbruk = summer(FASTE_UTGIFTER) + summer(KREDITTKORT);
    const netto = inn - forbruk;
    return {
      name: MAANED_KORT[i],
      inn: Math.round(inn),
      forbruk: Math.round(forbruk),
      netto: Math.round(netto),
      sparerate: inn > 0 ? (netto / inn) * 100 : null,
      gjennomfort: i < gjennomfortTom,
      harFaktisk,
    };
  });

  const totInn = maaneder.reduce((s, m) => s + m.inn, 0);
  const totForbruk = maaneder.reduce((s, m) => s + m.forbruk, 0);
  const totNetto = totInn - totForbruk;

  // Hittil i år: jan t.o.m. forrige avsluttede måned (inneværende måned er
  // ufullstendig og ville gitt misvisende sparerate)
  const nå = new Date();
  const hittilTom = nå.getFullYear() === AAR ? Math.max(1, nå.getMonth()) : 12; // antall måneder
  const htInn = maaneder.slice(0, hittilTom).reduce((s, m) => s + m.inn, 0);
  const htNetto = maaneder.slice(0, hittilTom).reduce((s, m) => s + m.netto, 0);
  const htRate = htInn > 0 ? (htNetto / htInn) * 100 : null;
  const htOK = htRate !== null && sparemaal !== null && htRate >= sparemaal;

  // Generer CFO-notat: send KUN kategorinavn og aggregerte beløp til serveren,
  // for tre perioder — hittil i år, inneværende måned og prognose helår
  const genererNotat = async () => {
    if (htRate === null || sparemaal === null) { setNotatFeil('Mangler sparerate eller sparemål'); return; }
    setGenererer(true); setNotatFeil(null);
    const utgifter = Object.entries({ ...FASTE_UTGIFTER, ...KREDITTKORT })
      .filter(([kat]) => kat !== 'Jobbutlegg Terje'); // refunderes — ikke reelt forbruk
    const curIdxNotat = nå.getFullYear() === AAR ? nå.getMonth() : null;

    // Hittil i år (jan t.o.m. forrige avsluttede måned)
    const hittilKategorier = utgifter.map(([kat, v]) => {
      let sum = 0, budsjett = 0;
      for (let i = 0; i < hittilTom; i++) {
        const f = getFaktisk(kat, i);
        sum += f !== null ? f : (v.budsjett[i] || 0);
        budsjett += v.budsjett[i] || 0;
      }
      return { navn: kat, sum: Math.round(sum), budsjett: Math.round(budsjett) };
    });

    // Inneværende måned — kun registrerte faktiske tall, med snitt siste 3 mnd
    const maanedKategorier = [];
    if (curIdxNotat !== null && curIdxNotat >= 3) {
      for (const [kat, v] of utgifter) {
        const f = getFaktisk(kat, curIdxNotat);
        if (f === null || f <= 0) continue;
        const snitt3 = [curIdxNotat - 3, curIdxNotat - 2, curIdxNotat - 1]
          .reduce((s, i) => { const fi = getFaktisk(kat, i); return s + (fi !== null ? fi : (v.budsjett[i] || 0)); }, 0) / 3;
        maanedKategorier.push({ navn: kat, sum: Math.round(f), budsjett: Math.round(v.budsjett[curIdxNotat] || 0), snitt3mnd: Math.round(snitt3) });
      }
    }

    // Prognose helår — faktisk der det finnes, ellers budsjett
    const prognoseKategorier = utgifter.map(([kat, v]) => {
      let sum = 0;
      for (let i = 0; i < 12; i++) {
        const f = getFaktisk(kat, i);
        sum += f !== null ? f : (v.budsjett[i] || 0);
      }
      return { navn: kat, sum: Math.round(sum), budsjett: Math.round(v.budsjett.reduce((a, b) => a + b, 0)) };
    });

    try {
      const r = await fetch('/api/cfo/notat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-token': token },
        body: JSON.stringify({
          sparerate: { faktisk: Math.round(htRate * 10) / 10, maal: sparemaal },
          perioder: {
            hittil: { label: `jan–${MAANEDER[hittilTom - 1].toLowerCase()} ${AAR}`, kategorier: hittilKategorier },
            maaned: { label: curIdxNotat !== null ? `${MAANEDER[curIdxNotat].toLowerCase()} ${AAR}` : `${AAR}`, kategorier: maanedKategorier },
            prognose: { label: `${AAR}`, kategorier: prognoseKategorier, inntekt: totInn, forbruk: totForbruk, netto: totNetto },
          },
        }),
      }).then(r => r.json());
      if (r.ok && r.notat) setNotat(r.notat); // tråden beholdes — samtalen overlever nytt notat
      else setNotatFeil(r.error || 'Generering feilet');
    } catch { setNotatFeil('Nettverksfeil ved generering'); }
    setGenererer(false);
  };

  // ── Kategorivarsler ──────────────────────────────────────────────────────
  // Flagg utgiftskategorier der inneværende måned (kun registrerte faktiske
  // tall) ligger mer enn varselProsent over snittet av de 3 siste fullførte
  // månedene (faktisk der det finnes, ellers budsjett).
  const curIdx = nå.getFullYear() === AAR ? nå.getMonth() : null;
  const kanVarsle = curIdx !== null && curIdx >= 3 && varselProsent !== null;
  const varsler = [];
  if (kanVarsle) {
    const utgiftsGrupper = { ...FASTE_UTGIFTER, ...KREDITTKORT };
    for (const [kat, v] of Object.entries(utgiftsGrupper)) {
      // Jobbutlegg Terje refunderes av arbeidsgiver — ikke reelt familieforbruk
      if (kat === 'Jobbutlegg Terje') continue;
      const cur = getFaktisk(kat, curIdx);
      if (cur === null || cur <= 0) continue;
      const forrige3 = [curIdx - 3, curIdx - 2, curIdx - 1].map(i => {
        const f = getFaktisk(kat, i);
        return f !== null ? f : (v.budsjett[i] || 0);
      });
      const snitt = forrige3.reduce((a, b) => a + b, 0) / 3;
      if (snitt <= 0) continue;
      const overProsent = ((cur - snitt) / snitt) * 100;
      if (overProsent > varselProsent) varsler.push({ kat, cur, snitt, overProsent });
    }
    varsler.sort((a, b) => (b.cur - b.snitt) - (a.cur - a.snitt));
  }
  const snittPeriode = kanVarsle ? `${MAANEDER[curIdx - 3].toLowerCase()}–${MAANEDER[curIdx - 1].toLowerCase()}` : '';

  const status = (m) =>
    m.gjennomfort ? { tekst: 'Gjennomført', bg: C.greenL, col: C.green }
    : m.harFaktisk ? { tekst: 'Pågår', bg: C.blueL, col: C.blue }
    : { tekst: 'Prognose', bg: C.surface2, col: C.muted };

  const ratePille = (rate) => {
    if (rate === null || sparemaal === null) return <span style={{ color: C.muted, fontSize: 12 }}>—</span>;
    const ok = rate >= sparemaal;
    return (
      <span style={{ ...mono, fontSize: 12, fontWeight: 700, background: ok ? C.greenL : C.redL, color: ok ? C.green : C.red, padding: '3px 10px', borderRadius: 20 }}>
        {formatProsent(rate)}
      </span>
    );
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const v = payload[0].value;
    return (
      <div style={{ ...card(), padding: '10px 14px', fontSize: 12 }}>
        <div style={{ color: C.muted, marginBottom: 4 }}>{label}</div>
        <div style={{ ...mono, fontWeight: 600, color: v >= 0 ? C.green : C.red }}>{formatNOK(v, true)}</div>
      </div>
    );
  };

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontFamily: "'Syne', sans-serif", fontSize: 22, fontWeight: 700, letterSpacing: '-0.5px', margin: 0 }}>Cash flow {AAR}</h1>
        <p style={{ color: C.muted, fontSize: 13, margin: '4px 0 0' }}>
          Inntekt, forbruk og netto per måned · faktiske tall der de finnes, ellers budsjett
        </p>
      </div>

      {/* Kategorivarsler — øverst i CFO-visningen */}
      {kanVarsle && (
        <div style={{ ...card(), marginBottom: 12, borderLeft: `4px solid ${varsler.length > 0 ? C.red : C.green}` }}>
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: 10, marginBottom: varsler.length > 0 ? 12 : 0 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              {varsler.length > 0
                ? `⚠ ${varsler.length} kategorivarsel${varsler.length > 1 ? 'er' : ''} — ${MAANEDER[curIdx].toLowerCase()} mot snitt ${snittPeriode}`
                : `✓ Ingen kategorier over varselgrensen i ${MAANEDER[curIdx].toLowerCase()}`}
            </div>
            <div>
              {redigerer === 'varsel' ? editor('varselProsent') : (
                <button
                  onClick={() => startRediger('varsel', varselProsent, 25)}
                  style={{ background: C.surface2, color: C.text, border: `1px solid ${C.border}`, borderRadius: 8, padding: '6px 12px', fontSize: 12, cursor: 'pointer' }}
                >
                  Grense: <strong style={mono}>{varselProsent !== null ? formatProsent(varselProsent) : '…'}</strong> over snitt ✎
                </button>
              )}
              {feil && redigerer === 'varsel' && <div style={{ fontSize: 11, color: C.red, marginTop: 6 }}>{feil}</div>}
            </div>
          </div>
          {varsler.map(v => (
            <div key={v.kat} style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: 4, padding: '9px 12px', background: C.redL, borderRadius: 8, marginBottom: 6 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
              <span onClick={() => setTxKategori(v.kat)} style={{ cursor: 'pointer', textDecoration: 'underline' }} title="Vis transaksjoner">{v.kat}</span>
              <span style={{ ...mono, marginLeft: 10, fontSize: 12, fontWeight: 700, color: C.red }}>+{formatProsent(v.overProsent)}</span>
            </div>
            <div style={{ ...mono, fontSize: 12, color: C.muted }}>
              {formatNOK(v.cur)} nå · snitt {snittPeriode}: {formatNOK(Math.round(v.snitt))}
            </div>
            </div>
          ))}
        </div>
      )}

      {/* KPI-kort — netto fremhevet */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1.4fr', gap: 12, marginBottom: 12 }}>
        <div style={{ ...card(), borderTop: `3px solid ${C.green}` }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 5 }}>Inntekt i år</div>
          <div style={{ ...mono, fontSize: 20, fontWeight: 600, color: C.green }}>{formatNOK(totInn)}</div>
        </div>
        <div style={{ ...card(), borderTop: `3px solid ${C.blue}` }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 5 }}>Forbruk i år</div>
          <div style={{ ...mono, fontSize: 20, fontWeight: 600, color: C.blue }}>{formatNOK(totForbruk)}</div>
        </div>
        <div style={{ ...card(), borderTop: `4px solid ${totNetto >= 0 ? C.green : C.red}`, background: totNetto >= 0 ? C.greenL : C.redL }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 5, fontWeight: 600 }}>NETTO I ÅR</div>
          <div style={{ ...mono, fontSize: 26, fontWeight: 700, color: totNetto >= 0 ? C.green : C.red }}>{formatNOK(totNetto, true)}</div>
        </div>
      </div>

      {/* Sparerate — hittil i år mot mål + redigerbart sparemål */}
      <div style={{ ...card(), marginBottom: 12, borderLeft: `4px solid ${sparemaal === null ? C.border : htOK ? C.green : C.red}` }}>
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 6 }}>
              Sparerate hittil i år (jan–{MAANEDER[hittilTom - 1].toLowerCase()})
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
              <span style={{ ...mono, fontSize: 28, fontWeight: 700, color: sparemaal === null ? C.text : htOK ? C.green : C.red }}>
                {formatProsent(htRate)}
              </span>
              {sparemaal !== null && htRate !== null && (
                <span style={{ fontSize: 12, fontWeight: 600, color: htOK ? C.green : C.red }}>
                  {htOK ? '✓ over mål' : '✕ under mål'} ({formatProsent(htRate - sparemaal)} vs. mål)
                </span>
              )}
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
              Spart {formatNOK(htNetto)} av {formatNOK(htInn)} inntekt
            </div>
          </div>
          <div style={{ textAlign: isMobile ? 'left' : 'right' }}>
            {redigerer === 'sparemaal' ? editor('sparemaalProsent') : (
              <button
                onClick={() => startRediger('sparemaal', sparemaal, 10)}
                style={{ background: C.surface2, color: C.text, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 14px', fontSize: 13, cursor: 'pointer' }}
              >
                Mål: <strong style={mono}>{sparemaal !== null ? formatProsent(sparemaal) : '…'}</strong> av inntekt ✎
              </button>
            )}
            {feil && redigerer === 'sparemaal' && <div style={{ fontSize: 11, color: C.red, marginTop: 6 }}>{feil}</div>}
          </div>
        </div>
      </div>

      {/* CFO-notat — generert via Anthropic API */}
      <div style={{ ...card(), marginBottom: 12, borderLeft: `4px solid ${C.blue}` }}>
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: 10, marginBottom: notat ? 12 : 0 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            💼 CFO-notat
            {notat?.generert && (
              <span style={{ marginLeft: 10, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
                generert {new Date(notat.generert).toLocaleString('no-NO', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>
          <button
            onClick={genererNotat}
            disabled={genererer}
            style={{ background: C.blue, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 12, fontWeight: 600, cursor: genererer ? 'wait' : 'pointer', opacity: genererer ? 0.7 : 1 }}
          >
            {genererer ? 'Genererer…' : notat ? '↻ Generer på nytt' : '✨ Generer CFO-notat'}
          </button>
        </div>
        {notatFeil && <div style={{ fontSize: 12, color: C.red, marginTop: 8 }}>{notatFeil}</div>}
        {notat?.seksjoner && (
          <div>
            {[
              { tittel: `Hittil i år (${notat.perioder?.hittil || ''})`, tekst: notat.seksjoner.hittilIAar },
              { tittel: `Inneværende måned (${notat.perioder?.maaned || ''})`, tekst: notat.seksjoner.innevaerendeMaaned },
              { tittel: `Prognose ut året (${notat.perioder?.prognose || ''})`, tekst: notat.seksjoner.prognoseUtAaret },
            ].filter(s => s.tekst).map(s => (
              <div key={s.tittel} style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.blue, marginBottom: 3 }}>{s.tittel}</div>
                <div style={{ fontSize: 13, lineHeight: 1.65, color: C.text }}>{s.tekst}</div>
              </div>
            ))}
            {notat.seksjoner.raad && (
              <div style={{ background: C.blueL, borderRadius: 8, padding: '10px 14px', marginTop: 4 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.blue, marginBottom: 3 }}>💡 Råd</div>
                <div style={{ fontSize: 13, lineHeight: 1.65, color: C.text }}>{notat.seksjoner.raad}</div>
              </div>
            )}
          </div>
        )}
        {!notat?.seksjoner && notat?.tekst && (
          <div style={{ fontSize: 13, lineHeight: 1.65, color: C.text, whiteSpace: 'pre-wrap' }}>{notat.tekst}</div>
        )}
        {/* Oppfølgingsspørsmål — mini-tråd med notatet + grunnlaget som kontekst */}
        {notat?.seksjoner && (
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
            {cfoTraad.map((m, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: m.rolle === 'bruker' ? 'flex-end' : 'flex-start', marginBottom: 8 }}>
                <div style={{
                  maxWidth: '88%', padding: '8px 12px', fontSize: 13, lineHeight: 1.55, wordBreak: 'break-word',
                  borderRadius: m.rolle === 'bruker' ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                  background: m.rolle === 'bruker' ? C.dark : C.surface2,
                  color: m.rolle === 'bruker' ? '#fff' : C.text,
                }}>{m.tekst}</div>
              </div>
            ))}
            {sporLaster && (
              <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 8 }}>
                <div style={{ padding: '8px 12px', borderRadius: '12px 12px 12px 4px', background: C.surface2, color: C.muted, fontSize: 13 }}>Tenker…</div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                value={cfoSporsmal}
                onChange={e => setCfoSporsmal(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') stillSporsmal(); }}
                placeholder="Still et oppfølgingsspørsmål til notatet…"
                maxLength={1000}
                style={{ flex: 1, padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: "'DM Sans', sans-serif", outline: 'none', background: C.surface, minWidth: 0 }}
              />
              <button
                onClick={stillSporsmal}
                disabled={sporLaster || !cfoSporsmal.trim()}
                style={{ background: sporLaster || !cfoSporsmal.trim() ? C.surface2 : C.blue, color: sporLaster || !cfoSporsmal.trim() ? C.muted : '#fff', border: 'none', borderRadius: 8, padding: '0 16px', fontSize: 13, fontWeight: 600, cursor: sporLaster || !cfoSporsmal.trim() ? 'default' : 'pointer', minHeight: 38, flexShrink: 0 }}
              >Spør</button>
            </div>
            {cfoTraad.length > 0 && (
              <div style={{ textAlign: 'right', marginTop: 8 }}>
                <button onClick={nullstillTraad} style={{ background: 'transparent', border: 'none', color: C.muted, fontSize: 11, cursor: 'pointer', textDecoration: 'underline', padding: 4 }}>
                  🗑 Nullstill samtale
                </button>
              </div>
            )}
          </div>
        )}
        {!notat && !notatFeil && !genererer && (
          <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>
            Genererer et kort notat med de største innsparingsmulighetene, sparerate mot mål og ett konkret råd.
            Kun kategorinavn og beløp sendes til Anthropic API-et.
          </div>
        )}
      </div>

      {/* Netto per måned — søylediagram */}
      <div style={{ ...card(), marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: C.muted, marginBottom: 14 }}>Netto per måned</div>
        <ResponsiveContainer width="100%" height={isMobile ? 160 : 200}>
          <BarChart data={maaneder} barSize={20}>
            <XAxis dataKey="name" tick={{ fontSize: isMobile ? 9 : 10, fill: C.muted }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: C.muted }} axisLine={false} tickLine={false} tickFormatter={v => `${Math.round(v / 1000)}k`} width={36} />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine y={0} stroke={C.border} />
            <Bar dataKey="netto" radius={[3, 3, 0, 0]}>
              {maaneder.map((m, i) => (
                <Cell key={i} fill={m.netto >= 0 ? C.green : C.red} opacity={m.gjennomfort || m.harFaktisk ? 1 : 0.45} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Månedstabell — netto og sparerate fremhevet */}
      <div style={card()}>
        <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 10 }}>Måned for måned</div>
        <div className="scroll-x">
          <div style={{ minWidth: 560, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1.2fr 1fr 100px', fontSize: 10, fontWeight: 600, color: C.muted, letterSpacing: '0.05em', textTransform: 'uppercase', padding: '4px 0', borderBottom: `1px solid ${C.border}` }}>
            {['Måned', 'Inntekt', 'Forbruk', 'Netto', 'Sparerate', 'Status'].map((h, i) => (
              <div key={i} style={{ textAlign: i === 0 ? 'left' : 'right' }}>{h}</div>
            ))}
          </div>
          {maaneder.map((m, i) => {
            const st = status(m);
            return (
              <div key={i} style={{ minWidth: 560, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1.2fr 1fr 100px', alignItems: 'center', padding: '7px 0', borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? 'transparent' : C.surface2 }}>
                <div style={{ fontSize: 12 }}>{m.name}</div>
                <div style={{ ...mono, fontSize: 12, textAlign: 'right', color: C.green }}>{formatNOK(m.inn)}</div>
                <div style={{ ...mono, fontSize: 12, textAlign: 'right', color: C.blue }}>{formatNOK(m.forbruk)}</div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ ...mono, fontSize: 12, fontWeight: 700, background: m.netto >= 0 ? C.greenL : C.redL, color: m.netto >= 0 ? C.green : C.red, padding: '3px 10px', borderRadius: 20 }}>
                    {formatNOK(m.netto, true)}
                  </span>
                </div>
                <div style={{ textAlign: 'right' }}>{ratePille(m.sparerate)}</div>
                <div style={{ textAlign: 'right', fontSize: 11 }}>
                  <span style={{ background: st.bg, color: st.col, padding: '2px 8px', borderRadius: 20 }}>{st.tekst}</span>
                </div>
              </div>
            );
          })}
          {/* Totalrad */}
          <div style={{ minWidth: 560, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1.2fr 1fr 100px', alignItems: 'center', padding: '9px 0', background: C.dark, borderRadius: 6, marginTop: 4 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#fff', paddingLeft: 4 }}>Totalt</div>
            <div style={{ ...mono, fontSize: 12, fontWeight: 600, color: '#fff', textAlign: 'right' }}>{formatNOK(totInn)}</div>
            <div style={{ ...mono, fontSize: 12, fontWeight: 600, color: '#fff', textAlign: 'right' }}>{formatNOK(totForbruk)}</div>
            <div style={{ ...mono, fontSize: 13, fontWeight: 700, textAlign: 'right', color: totNetto >= 0 ? '#9FE1CB' : '#F0997B' }}>{formatNOK(totNetto, true)}</div>
            <div style={{ ...mono, fontSize: 12, fontWeight: 600, textAlign: 'right', color: '#fff' }}>{totInn > 0 ? formatProsent((totNetto / totInn) * 100) : '—'}</div>
            <div />
          </div>
        </div>
      </div>

      {txKategori && <KategoriTransaksjoner token={token} kategori={txKategori} onLukk={() => setTxKategori(null)} />}
    </div>
  );
}
